<template>
  <section>
    <LayoutAccount :hide-top-header="currentMode === 'email_token' && emailTokenStep === 'token-verification'">
      <template #title>
        {{ $t('pages.login.headline_title_line1') }} {{ $t('pages.login.headline_title_line2') }}
      </template>

      <template #mainContent>
         <div class="login-providers intro-section__form" :class="socialPositionClass">

          <!-- Primary Provider -->
          <div class="main-form">
          <!-- JWT Auto-Login -->
          <JwtAuthForm
              v-if="currentMode === 'jwt'"
              @success="handleLoginSuccess"
              @error="handleAuthError"
          />

          <!-- SSO Auto-Login -->
          <SsoAuthForm
              v-else-if="currentMode === 'sso'"
              @success="handleLoginSuccess"
              @error="handleAuthError"
          />

          <!-- Magic Link Auto-Verify (from email link) -->
          <MagicLinkAuthForm
              v-else-if="hasMagicLinkParams"
              @success="handleLoginSuccess"
              @error="handleAuthError"
          />

          <!-- Magic Link Form (manual email entry) -->
          <MagicLinkForm
              v-else-if="currentMode === 'magic_link'"
              @step-change="handleStepChange"
          />

            <EmailTokenUnifiedForm
                v-else-if="currentMode === 'email_token'"
                @success="handleLoginSuccess"
                @step-change="handleStepChange"
            />

            <PasswordSignupForm
                v-else-if="currentMode === 'password'"
                @success="handleLoginSuccess"
            />
          </div>

          <!-- Social Login Buttons -->
          <div class="social-buttons">
            <SocialAuthButtons
              :show="showSocialButtons"
              container-class="social-auth-buttons text-center"
              :title-key="socialPosition === 'top' ? 'forms.login_form.social_login_title_top' : undefined"
              :apple-disabled="appleDisabled"
              @success="handleLoginSuccess"
              @error="handleSocialError"
            />
          </div>
         </div>
      </template>
    </LayoutAccount>

    <B2BSection v-if="isPicaThirdLevelDomain" />

  </section>
</template>


<script>
import LayoutAccount from '@/components/LayoutAccount'
import B2BSection from "@/components/B2BSection.vue"
import EventService from '@/services/Event.js'
import {mapGetters} from "vuex"

// Auth provider components
import {
  EmailTokenUnifiedForm,
  MagicLinkForm,
  MagicLinkAuthForm,
  PasswordLoginForm,
  JwtAuthForm,
  SsoAuthForm,
} from '@/domain/auth'
import PasswordSignupForm from "@/domain/auth/components/providers/PasswordSignupForm.vue";
import SocialAuthButtons from '@/domain/auth/components/SocialAuthButtons.vue';

export default {
  components: {
    PasswordSignupForm,
    LayoutAccount,
    B2BSection,
    EmailTokenUnifiedForm,
    MagicLinkForm,
    MagicLinkAuthForm,
    SocialAuthButtons,
    JwtAuthForm,
    SsoAuthForm,
  },

  data() {
    return {
      isEventProvided: false,
      event: null, // for custom page only
      socialError: false,
      currentMode: null, // 'email_token' or 'password'
      emailTokenStep: 'email-input' // Track current step of email token form
    }
  },

  computed: {
    ...mapGetters(['isPicaThirdLevelDomain', 'thirdLevelDomain', 'disableSocialLoginInPreview', 'showMarketingCheck']),
    ...mapGetters('appConfig', ['getAppConfigReady', 'loginSubtitle']),
    ...mapGetters('auth', [
      'isAuthProviderEnabled',
      'primaryAuthProvider',
      'hasSocialLoginEnabled',
      'primaryAuthProviderWithQuery',
      'enabledAuthProvidersWithQuery'
    ]),

    currentEnabledProviders() {
      return this.enabledAuthProvidersWithQuery(this.$route.query)
    },

    hasMagicLinkParams() {
      return !!(this.$route.query.magic_link && this.$route.query.email)
    },

    appleDisabled() {
      return this.disableSocialLoginInPreview
    },

    showSocialButtons() {
      // Hide social buttons during auto-login flows
      if (this.currentMode === 'jwt' || this.currentMode === 'sso' || this.hasMagicLinkParams) {
        return false
      }

      // Show social buttons only on initial email step for email_token/magic_link
      if (this.currentMode === 'email_token' || this.currentMode === 'magic_link') {
        return this.emailTokenStep === 'email-input'
      }

      // Always show for password mode
      return true
    },

    socialPosition() {
      if (['photos'].includes(this.thirdLevelDomain)) return 'top'
      return 'bottom'
    },

    socialPositionClass() {
      return `social-position-${this.socialPosition}`
    },
  },

  mounted() {
    // Determine current mode from URL query param or use primary provider
    // Primary provider is now aware of ?jwt and ?t&k params and will auto-prioritize them
    const modeFromQuery = this.$route.query.mode

    if (modeFromQuery && this.isProviderEnabled(modeFromQuery)) {
      this.currentMode = modeFromQuery
    } else {
      // Use primary provider with query param awareness
      // This will auto-detect JWT (?jwt=xxx) or SSO (?t=xxx&k=yyy) and prioritize them
      this.currentMode = this.primaryAuthProviderWithQuery(this.$route.query)
    }
  },

  methods: {
    isProviderEnabled(provider) {
      return this.currentEnabledProviders.includes(provider)
    },

    handleStepChange(newStep) {
      this.emailTokenStep = newStep
    },

    handleLoginSuccess() {
      // Show marketing consent modal if needed (for new users)
      if (this.showMarketingCheck) {
        this.$bvModal.show('modal-marketing-consent')
      }

      // Redirect to checkins or intended page
      this.$router.push('/checkins')
    },

    handleSocialError(error) {
      console.error('Social login error:', error)
      this.socialError = true
      setTimeout(() => {
        this.socialError = false
      }, 5000)
    },

    handleAuthError(error) {
      console.error('Authentication error:', error)
      // On JWT/SSO auth failure, fallback to standard email_token form
      this.currentMode = this.primaryAuthProvider
    }
  },

  created() {
    if (this.$route.params.hasOwnProperty('eventSlug')) {
      const eventSlug = this.$route.params.eventSlug
      this.isEventProvided = !!eventSlug
      EventService.getEventBySlug(eventSlug).then(response => {
        if (response.data.count === 1) {
          this.event = response.data.results[0]
        }
      })
    }
  }
}
</script>